import { createSelector } from '@reduxjs/toolkit';
import { createCachedSelector } from 're-reselect';
import {
    get,
    getOr,
    map,
    pipe,
    filter,
    uniq,
    sortBy,
    identity,
    isNil,
    includes,
    sortedUniq,
    every,
    isEmpty,
    intersection,
    values
} from 'lodash/fp';
import { AssetStatus, AssetFilter } from 'app/lib/constants';
import {
    selectSnowflakeDatabasesNames,
    selectSnowflakeSchemas
} from 'app/store/snowflake/selectors.js';
import {
    selectUpstreamDataSourceFqnsByViewUid,
    selectDependencies
} from 'app/store/projects/selectors.js';
import { splitFqn, toStringFqn } from 'app/lib/utils';
import { selectSharesForCurrentProject } from 'app/store/shares/selectors.js';
import { selectDatasourcesFqnInCurrentProject } from 'app/store/addViews/selectors.js';
import { selectTableDetailsForSnowflakeLocations } from 'app/store/tableDetails/selectors.js';

const selectSlice = get('assets');

export const selectAssetFilter = createSelector(selectSlice, get('assetFilter'));

// Adds found database, schema and source fqn to the set
function addIdToSet(sourceStringFqn, set) {
    set.add(sourceStringFqn);
    const { database, schema } = splitFqn(sourceStringFqn);
    set.add(database);
    set.add(toStringFqn({ database, schema }));
}

function filterTableDetailsFqnsByPropName(tables, propName) {
    return tables.reduce((set, table) => {
        if (table[propName]) {
            addIdToSet(table.fqn, set);
        }
        return set;
    }, new Set());
}

const selectFilteredAssetIds = createSelector(
    selectAssetFilter,
    selectDatasourcesFqnInCurrentProject,
    selectTableDetailsForSnowflakeLocations,
    (assetFilter, upstreams, tables) => {
        switch (assetFilter) {
            case AssetFilter.ADDED: {
                const result = new Set();
                upstreams.forEach((item) => {
                    if (tables.find(({ fqn }) => fqn === item)) {
                        addIdToSet(item, result);
                    }
                });
                return result;
            }
            case AssetFilter.USED: {
                return filterTableDetailsFqnsByPropName(tables, 'isUsedByDatameer');
            }
            case AssetFilter.CREATED_BY_DATAMEER: {
                return filterTableDetailsFqnsByPropName(tables, 'isCreatedByDatameer');
            }
            case AssetFilter.SCHEDULED: {
                return filterTableDetailsFqnsByPropName(tables, 'isScheduled');
            }
            case AssetFilter.ALL:
            default:
                return null;
        }
    }
);

export const selectFilteredDatabases = createSelector(
    selectSnowflakeDatabasesNames,
    selectFilteredAssetIds,
    (names, ids) => (ids ? names.filter((name) => ids.has(name)) : names)
);

export const selectSelectedTargetFqn = createSelector(selectSlice, get('selectedTargetFqn'));

const selectSourceLocationsByFqn = createSelector(selectSnowflakeSchemas, values);

const selectFqnProp = (_, props) => props.fqn;

const selectDatabaseProp = (_, props) => props.database;
const selectSchemaProp = (_, props) => props.schema;

export const selectFilteredSchemasForDatabase = createCachedSelector(
    selectDatabaseProp,
    selectSourceLocationsByFqn,
    selectFilteredAssetIds,
    (database, schemas, ids) =>
        pipe(
            filter({ database }),
            filter(({ fqn }) => (ids ? ids.has(fqn) : true)),
            map('schema'),
            sortBy(identity),
            sortedUniq
        )(schemas)
)((_state_, props) => props.database);

export const selectSourceLocationByDatabaseAndSchema = createSelector(
    selectDatabaseProp,
    selectSchemaProp,
    selectSnowflakeSchemas,
    (database, schema, schemas) => schemas[toStringFqn({ database, schema })]
);

export const selectIsSourceLocationByDatabaseAndSchemaEmpty = createSelector(
    selectSourceLocationByDatabaseAndSchema,
    isEmpty
);

export const selectFilteredSourcesByDatabaseAndSchema = createCachedSelector(
    selectFilteredAssetIds,
    selectSourceLocationByDatabaseAndSchema,
    (ids, sourceLocation) =>
        pipe(
            filter((item) => (ids ? ids.has(item.fqn) : true)),
            sortBy('table')
        )(sourceLocation?.sources)
)((_state_, props) => `${props.database}.${props.schema}`);

export const selectAllSourcesByDatabaseAndSchema = createCachedSelector(
    selectSourceLocationByDatabaseAndSchema,
    (sourceLocation) => sourceLocation?.sources && Object.values(sourceLocation?.sources)
)((_state, props) => `${props.database}.${props.schema}`);

const selectDependencyUsageById = createSelector(
    selectUpstreamDataSourceFqnsByViewUid,
    selectDependencies,
    selectSharesForCurrentProject,
    (upstreamsFqnsByViewUid, dependencies, shares) => {
        const result = {};
        for (const viewId in upstreamsFqnsByViewUid) {
            for (const sourceFqn of upstreamsFqnsByViewUid[viewId]) {
                let status = AssetStatus.ADDED;
                if (shares?.some((share) => viewId === share.parentUid)) {
                    status = AssetStatus.USED;
                }
                if (dependencies.some((dependency) => viewId === dependency.fromView)) {
                    status = AssetStatus.USED;
                }

                result[toStringFqn(sourceFqn)] = {
                    status,
                    viewId
                };
            }
        }

        for (const dependency of dependencies) {
            const upstreamView = upstreamsFqnsByViewUid?.[dependency.toView];

            if (!upstreamView) {
                continue;
            }

            for (const sourceFqn of upstreamsFqnsByViewUid[dependency.toView]) {
                result[toStringFqn(sourceFqn)].status = AssetStatus.USED;
            }
        }
        return result;
    }
);

export const selectUnusedDataSources = createSelector(selectDependencyUsageById, (dataSources) =>
    filter((item) => item.status !== AssetStatus.USED, dataSources)
);

export const selectUnusedDataSourcesCount = createSelector(
    selectUnusedDataSources,
    (unusedSources) => unusedSources.length
);

const selectSourceCountLeftAllDatasets = createSelector(
    selectDependencyUsageById,
    selectFilteredSourcesByDatabaseAndSchema,
    (usage, sources) => {
        const usedInProjectByFqn = Object.keys(usage);
        const usedSourcesByFqn = map('fqn', sources);
        const usedSourcesOfSchema = intersection(usedInProjectByFqn, usedSourcesByFqn);

        return sources.length - usedSourcesOfSchema.length;
    }
);

const selectSourceCountLeftAddedToProject = createSelector(
    selectSourceLocationByDatabaseAndSchema,
    selectFilteredSourcesByDatabaseAndSchema,
    (sourceLocationByDatabaseAndSchema, filteredSourcesByDatabaseAndSchema) =>
        Object.keys(sourceLocationByDatabaseAndSchema.sources).length -
        filteredSourcesByDatabaseAndSchema.length
);

export const selectSourceCountLeftToAdd = createSelector(
    selectAssetFilter,
    selectSourceCountLeftAllDatasets,
    selectSourceCountLeftAddedToProject,
    (assetFilter, sourceCountLeftAllDatasets, sourceCountLeftAddedToProject) =>
        assetFilter === AssetFilter.ALL ? sourceCountLeftAllDatasets : sourceCountLeftAddedToProject
);

const DEFAULT_TABLE_USAGE = { status: AssetStatus.NONE };
export const selectTableUsageForPropFqn = createSelector(
    selectFqnProp,
    selectDependencyUsageById,
    getOr(DEFAULT_TABLE_USAGE)
);

const selectAddedSourceLocationFqn = createSelector(
    selectDatasourcesFqnInCurrentProject,
    (sourceFqns) => {
        const results = [];

        for (const sourceFqn of sourceFqns) {
            if (!isNil(sourceFqn)) {
                const { database, schema } = splitFqn(sourceFqn);
                results.push(toStringFqn({ database, schema }));
            }
        }

        return results;
    }
);

const selectAddedDatabases = createSelector(
    selectAddedSourceLocationFqn,
    (addedSourceLocationFqns) => {
        const results = addedSourceLocationFqns.map((source) => splitFqn(source)?.database);
        return uniq(results);
    }
);

export const selectIsDatabaseAdded = createSelector(
    selectDatabaseProp,
    selectAddedDatabases,
    (database, addedDatabases) => includes(database, addedDatabases)
);

const selectAddedSchemasForDatabase = createSelector(
    selectDatabaseProp,
    selectAddedSourceLocationFqn,
    (databaseProp, addedSourceLocationFqns) => {
        const results = [];
        for (const sourceLocationFqn of addedSourceLocationFqns) {
            const { database, schema } = splitFqn(sourceLocationFqn);
            if (databaseProp === database) {
                results.push(schema);
            }
        }

        return uniq(results);
    }
);

export const selectIsSchemaAdded = createSelector(
    selectSchemaProp,
    selectAddedSchemasForDatabase,
    (schema, addedSchemas) => includes(schema, addedSchemas)
);

const selectSourcesByDatabaseAndSchema = createSelector(
    selectSourceLocationByDatabaseAndSchema,
    (sourceLocation) => pipe(sortBy('table'))(sourceLocation?.sources)
);

export const selectIsSchemaFullyAdded = createSelector(
    selectDependencyUsageById,
    selectSourcesByDatabaseAndSchema,
    (dependencies, sources) => {
        if (isEmpty(sources)) {
            return false;
        }
        const schemaSourceFqns = map('fqn', sources);
        return every((fqn) => {
            const dependency = dependencies[fqn];

            return dependency && dependency.status !== AssetStatus.NONE;
        }, schemaSourceFqns);
    }
);

export const selectFlatCatalog = createSelector(selectSourceLocationsByFqn, (schemas) => {
    let result = [];
    schemas.forEach(({ sources }) => {
        if (!isEmpty(sources)) {
            result = [...result, ...values(sources)];
        }
    });
    return result;
});
